import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './App.css';

// i18n
import './i18n';

// Theme Provider
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { PrivacyProvider } from './contexts/PrivacyContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { AdminProvider, useAdmin } from './contexts/AdminContext';

// Components
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import DashboardNew from './components/DashboardNew';
import CanalManagerNew from './components/CanalManagerNew';
import Modelagem from './components/Modelagem';
import Prompts from './components/Prompts';
import PromptsPage from './components/PromptsPage';
import ReferenciasPage from './components/ReferenciasPage';
import ComunidadePage from './components/ComunidadePage';
import AcademiaPage from './components/AcademiaPage';
import TrustScorePage from './components/academia/TrustScorePage';
import Configuracoes from './components/Configuracoes';
import ConfiguracoesNew from './components/ConfiguracoesNew';
// ProfilePage merged into ConfiguracoesNew — /perfil redirects to /configuracoes
import MyPlanPage from './components/MyPlanPage';
import TextToolsPage from './components/TextToolsPage';
import SuperAdmin from './components/SuperAdminNew';  // ✅ Componente correto e atualizado
import VideosVirais from './components/VideosVirais';
import NichosViraisV2Page from './components/NichosViraisV2Page';
import NicheFinderPage from './components/niche-finder/NicheFinderPage';
import DobraNichoPage from './components/dobra-nicho/DobraNichoPage';
import DobraHistoryPage from './components/dobra-nicho/DobraHistoryPage';
import CanaisRemovidosPage from './components/CanaisRemovidosPage';
import GestaoFinanceira from './components/GestaoFinanceira';
import CategoriasPage from './components/CategoriasPage';
import InsightsPage from './components/InsightsPage';
import NotificationManagerPage from './components/NotificationManagerPage';
import QuadroIdeiasPageKanban from './components/QuadroIdeiasPageKanban';
import AudioGeneratorPage from './components/AudioGeneratorPage';
import TranscricaoPage from './components/TranscricaoPage';
import MediaLibraryPage from './components/media-library/MediaLibraryPage';
import ApiDocsPage from './components/ApiDocsPage';
import DarkPlannerAppPage from './components/DarkPlannerAppPage';
import ExtensionDashboard from './components/ExtensionDashboard';
import HelpCenterPage from './components/help/HelpCenterPage';
import HelpWidget from './components/help/HelpWidget';
import WelcomeModal from './components/WelcomeModal';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios Interceptor - Tratar 403 (premium estruturado vs assinatura)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 403) {
      const data = error.response.data;
      const detail = data?.detail;

      // FASE 3 — 403 ESTRUTURADO de feature premium (videos_virais/nichos_virais/
      // insights_canal/lista_nichos). NÃO expulsa e NÃO abre modal: as 4 páginas
      // premium JÁ mostram o PremiumFeatureBlock embutido via gate próprio (selo),
      // então o 403 das chamadas de DADOS não deve saltar um modal por cima (evita
      // duplo bloqueio). Só rejeita — a página trata em silêncio.
      // (O modal do ÁUDIO Free é disparado à parte pelo AudioGeneratorPage —
      //  feature 'audio_free', via FREE_AUDIO_LIMIT — e NÃO passa por aqui.)
      const errorCode = (detail && typeof detail === 'object' ? detail.error_code : null) || data?.error_code;
      if (errorCode === 'PREMIUM_REQUIRED') {
        return Promise.reject(error);
      }

      // [tela de compra removida] 403 de assinatura NÃO redireciona mais pra /meu-plano.
      // Free/expirado/cancelado navega normalmente; o erro é apenas rejeitado abaixo.
    }
    return Promise.reject(error);
  }
);

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Route guard (Fase 3 — login permanente): usuário autenticado NAVEGA mesmo
// sem plano ativo. O bloqueio premium é por página/endpoint (selo /
// PREMIUM_REQUIRED), não mais por redirecionamento de rota.
function ProtectedRoute({ children }) {
  const { isLoading } = useAdmin();

  // Enquanto carrega dados de subscription, mostrar spinner
  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--dp-bg-primary)' }}>
        <div style={{ width: '48px', height: '48px', border: '3px solid var(--dp-border-subtle)', borderTop: '3px solid var(--dp-blue-500)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
      </div>
    );
  }

  return children;
}

// Modal de "recurso premium" (Fase 3) — disparado pelo interceptor 403 PREMIUM_REQUIRED.
const PREMIUM_FEATURE_LABELS = {
  videos_virais: 'Vídeos Virais',
  nichos_virais: 'Nichos Virais',
  dobra_nicho: 'Dobra de Nicho',
  insights_canal: 'Insights de Canal',
  lista_nichos: 'Lista de Nichos',
  tendencias: 'Tendências',
};
function PremiumRequiredModal({ feature, onClose }) {
  const { t } = useTranslation();
  const isFreeAudio = feature === 'audio_free';   // FASE 4: free esgotou os 3 áudios vitalícios
  // Recurso premium genérico → texto ÚNICO premium.* (PRO e PLUS). O free de áudio
  // mantém a mensagem específica dos 3 áudios vitalícios.
  const title = isFreeAudio ? 'Você usou seus 3 áudios gratuitos' : t('premium.title');
  const desc = isFreeAudio
    ? 'São 3 áudios gratuitos no total e eles não renovam. Assine um plano para gerar mais áudios.'
    : t('premium.message');
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--dp-bg-secondary, #11151f)', border: '1px solid var(--dp-border-subtle, #1e2533)', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%', textAlign: 'center', color: 'var(--dp-text-primary, #e6edf6)' }}>
        <div style={{ width: 56, height: 56, margin: '0 auto 14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,174,255,0.10)', border: '1px solid rgba(13,174,255,0.20)' }}>
          <Lock size={24} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>{title}</h2>
        <p style={{ margin: '0 0 20px', color: 'var(--dp-text-secondary, #97a3b6)' }}>{desc}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onClose} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid var(--dp-border-subtle, #1e2533)', background: 'transparent', color: 'var(--dp-text-secondary, #97a3b6)', cursor: 'pointer' }}>Entendi</button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [premiumModal, setPremiumModal] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token validity and get user info
      axios.get(`${API}/auth/me`)
        .then((response) => {
          setCurrentUser(response.data);
          setIsAuthenticated(true);
          // Modal no 1º acesso OU sempre que faltar senha (criar senha é OBRIGATÓRIO:
          // quem entrou por Google precisa definir uma para logar depois / no desktop).
          if (response.data.welcome_modal_seen === false || response.data.has_password === false) {
            setShowWelcome(true);
          }
        })
        .catch((error) => {
          if (error.response?.status === 403) {
            // Plano expirado — manter autenticado para poder ver /meu-plano
            setIsAuthenticated(true);
            setCurrentUser({ expired: true });
          } else {
            // Token inválido (401) ou erro de rede — deslogar
            localStorage.removeItem('token');
            setIsAuthenticated(false);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // FASE 3: o interceptor dispara 'premium-required' ao receber PREMIUM_REQUIRED.
  useEffect(() => {
    const onPremium = (e) => setPremiumModal(e.detail?.feature || true);
    window.addEventListener('premium-required', onPremium);
    return () => window.removeEventListener('premium-required', onPremium);
  }, []);

  const handleLogin = (token) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    // Notify AdminContext to re-fetch subscription data with new token
    window.dispatchEvent(new Event('auth-login'));
    // Fetch user data and check welcome modal
    axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((response) => {
        setCurrentUser(response.data);
        // Modal no 1º acesso OU sempre que faltar senha (criar senha é obrigatório).
        if (response.data.welcome_modal_seen === false || response.data.has_password === false) {
          setShowWelcome(true);
        }
      })
      .catch(() => {});
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  // Logout disparado pelo menu do avatar no Header (evento global, mesmo padrão
  // do 'auth-login') — evita passar onLogout por prop a todas as páginas.
  useEffect(() => {
    window.addEventListener('auth-logout', handleLogout);
    return () => window.removeEventListener('auth-logout', handleLogout);
  }, []);

  const handleWelcomeActivate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/welcome-seen`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Erro ao salvar welcome:', err);
    }
    setShowWelcome(false);
    setCurrentUser(prev => prev ? { ...prev, welcome_modal_seen: true } : prev);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--dp-bg-primary)' }}>
        <div style={{ width: '48px', height: '48px', border: '3px solid var(--dp-border-subtle)', borderTop: '3px solid var(--dp-blue-500)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <PrivacyProvider>
        <SidebarProvider>
          <AdminProvider>
            <div className="App">
            <WelcomeModal
              isOpen={showWelcome}
              onActivate={handleWelcomeActivate}
              hasPassword={currentUser?.has_password !== false}
              hasGoogle={currentUser?.has_google !== false}
              onPasswordCreated={() => setCurrentUser((prev) => (prev ? { ...prev, has_password: true } : prev))}
              onGoogleLinked={() => setCurrentUser((prev) => (prev ? { ...prev, has_google: true } : prev))}
            />
            {premiumModal && (
              <PremiumRequiredModal
                feature={typeof premiumModal === 'string' ? premiumModal : null}
                onClose={() => setPremiumModal(null)}
              />
            )}
            <BrowserRouter>
            <Routes>
            <Route
              path="/login"
              element={
                !isAuthenticated ?
                <Login onLogin={handleLogin} /> :
                <Navigate to="/" replace />
              }
            />
            <Route 
              path="/forgot-password" 
              element={<ForgotPassword />} 
            />
            <Route 
              path="/reset-password" 
              element={<ResetPassword />} 
            />

            <Route
              path="/"
              element={
                isAuthenticated ?
                <ProtectedRoute><DashboardNew onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/canais"
              element={
                isAuthenticated ?
                <ProtectedRoute><CanalManagerNew onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/modelagem"
              element={
                isAuthenticated ?
                <ProtectedRoute><Modelagem onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/prompts"
              element={
                isAuthenticated ?
                <ProtectedRoute><PromptsPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/text-tools"
              element={
                isAuthenticated ?
                <ProtectedRoute><TextToolsPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            {/* Redirects from old text tool routes */}
            <Route path="/gerar-srt" element={<Navigate to="/text-tools" replace />} />
            <Route path="/editor-texto" element={<Navigate to="/text-tools" replace />} />
            <Route path="/divisor-texto" element={<Navigate to="/text-tools" replace />} />
            <Route
              path="/configuracoes"
              element={
                isAuthenticated ?
                <ProtectedRoute><ConfiguracoesNew onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/perfil"
              element={<Navigate to="/configuracoes" replace />}
            />
            <Route
              path="/meu-plano"
              element={
                isAuthenticated ?
                <MyPlanPage onLogout={handleLogout} /> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/api-docs"
              element={
                isAuthenticated ?
                <ApiDocsPage onLogout={handleLogout} /> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/super-admin"
              element={
                isAuthenticated ?
                <ProtectedRoute><SuperAdmin onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/referencias"
              element={
                isAuthenticated ?
                <ProtectedRoute><ReferenciasPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/comunidade"
              element={
                isAuthenticated ?
                <ProtectedRoute><ComunidadePage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/academia"
              element={
                isAuthenticated ?
                <ProtectedRoute><AcademiaPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route path="/tutoriais" element={<Navigate to="/academia" replace />} />
            <Route
              path="/academia/score-de-confianca"
              element={
                isAuthenticated ?
                <ProtectedRoute><TrustScorePage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/central-de-ajuda"
              element={
                isAuthenticated ?
                <ProtectedRoute><HelpCenterPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route path="/ajuda" element={<Navigate to="/central-de-ajuda" replace />} />
            <Route
              path="/videos-virais"
              element={
                isAuthenticated ?
                <ProtectedRoute><VideosVirais onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/nichos-virais"
              element={
                isAuthenticated ?
                <ProtectedRoute><NicheFinderPage onLogout={handleLogout} isSuperAdmin={currentUser?.is_super_admin} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/dobra-nicho"
              element={
                isAuthenticated ?
                <ProtectedRoute><DobraNichoPage onLogout={handleLogout} isSuperAdmin={currentUser?.is_super_admin} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/dobra-nicho/historico"
              element={
                isAuthenticated ?
                <ProtectedRoute><DobraHistoryPage onLogout={handleLogout} isSuperAdmin={currentUser?.is_super_admin} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/nichos-virais-legacy"
              element={
                isAuthenticated ?
                <ProtectedRoute><NichosViraisV2Page onLogout={handleLogout} isSuperAdmin={currentUser?.is_super_admin} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/canais-removidos"
              element={
                isAuthenticated ?
                <ProtectedRoute><CanaisRemovidosPage onLogout={handleLogout} isSuperAdmin={currentUser?.is_super_admin} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/gestao-financeira"
              element={
                isAuthenticated ?
                <ProtectedRoute><GestaoFinanceira onLogout={handleLogout} isAdmin={currentUser?.is_super_admin} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/insights"
              element={
                isAuthenticated ?
                <ProtectedRoute><InsightsPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/quadro-ideias"
              element={
                isAuthenticated ?
                <ProtectedRoute><QuadroIdeiasPageKanban onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/audio-generator"
              element={
                isAuthenticated ?
                <ProtectedRoute><AudioGeneratorPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/transcricao"
              element={
                isAuthenticated ?
                <ProtectedRoute><TranscricaoPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/biblioteca-midia"
              element={
                isAuthenticated ?
                <ProtectedRoute><MediaLibraryPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/darkplanner-app"
              element={
                isAuthenticated ?
                <ProtectedRoute><DarkPlannerAppPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/extension"
              element={
                isAuthenticated ?
                <ProtectedRoute><ExtensionDashboard /></ProtectedRoute> :
                <Navigate to="/login" replace />
              }
            />
            <Route
              path="/notifications"
              element={
                isAuthenticated && currentUser?.is_super_admin ?
                <ProtectedRoute><NotificationManagerPage onLogout={handleLogout} /></ProtectedRoute> :
                <Navigate to="/login" replace />
              } 
            />
          </Routes>
          {/* Widget de atendimento (Planner) — global, só para usuário autenticado.
              Dentro do BrowserRouter para usar navegação (atalho à Central de Ajuda). */}
          {isAuthenticated && (
            <HelpWidget
              userName={currentUser?.full_name || currentUser?.username}
              userAvatar={currentUser?.avatar_url}
            />
          )}
        </BrowserRouter>
        <Toaster />
        </div>
        </AdminProvider>
        </SidebarProvider>
      </PrivacyProvider>
    </ThemeProvider>
  );
}

export default App;